import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { LINE_STATES, PRIORIDADES, TIPOS, eur, tempoDesde, fmtData, formatMatricula, numRef } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, MoreVertical, History, Layers, RotateCcw, ShieldCheck, ExternalLink, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { NotasPedido } from "@/components/NotasPedido";
import { OrcamentoCliente } from "@/components/OrcamentoCliente";
import { PrazoLinha } from "@/components/PrazoLinha";
import { OrigemPicker } from "@/components/FornTag";
import { VinChip } from "@/components/VinChip";
import { EscolherCliente } from "@/components/EscolherCliente";
import { ProximoPasso } from "@/components/ProximoPasso";
import { useAuth } from "@/context/AuthContext";

const NEXT_STATES = ["aguarda_resposta", "aprovado", "faturado", "entregue", "cancelado", "sem_resposta"];
const AUTO_STATES = ["pendente", "em_cotacao", "aguarda_fornecedor", "em_transito", "recebido"];

export default function AtendimentoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [fornecedores, setFornecedores] = useState([]);
  const [cancelDlg, setCancelDlg] = useState(null); // linha id
  const [motivo, setMotivo] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [encDlg, setEncDlg] = useState(false);
  const [dgDlg, setDgDlg] = useState(null); // { lid, tipo, descricao }
  const [dgMotivo, setDgMotivo] = useState("");
  const [dgSubst, setDgSubst] = useState(null);
  const [encForn, setEncForn] = useState("");
  const [encOrigem, setEncOrigem] = useState("");
  const [selLinhas, setSelLinhas] = useState({});
  const [identDlg, setIdentDlg] = useState(false);
  const [identNome, setIdentNome] = useState("");
  const [identAviso, setIdentAviso] = useState(null);
  const [identMat, setIdentMat] = useState("");
  const [gravando, setGravando] = useState(0);
  const fila = useRef(Promise.resolve());

  const load = useCallback(async () => {
    const { data } = await api.get(`/atendimentos/${id}`);
    setA(data);
  }, [id]);

  useEffect(() => {
    load();
    api.get("/fornecedores").then((r) => setFornecedores(r.data));
  }, [load]);

  useEffect(() => {
    if (!gravando) { return undefined; }
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => { window.removeEventListener("beforeunload", h); };
  }, [gravando]);

  if (!a) return <div className="text-slate-400 text-sm p-8">A carregar…</div>;

  const total = (a.linhas || []).reduce((s, l) => s + (l.total || 0), 0);
  const temFaturada = (a.linhas || []).some((l) => ["faturado", "entregue"].includes(l.estado));
  const podeEditarIdent = a.estado !== "concluido" && !temFaturada;

  const abrirIdent = () => {
    setIdentNome(a.cliente?.nome || "");
    setIdentMat(a.veiculo?.matricula || "");
    setIdentAviso(null);
    setIdentDlg(true);
  };
  const guardarMatricula = async () => {
    if (a.veiculo?.id && identMat !== (a.veiculo?.matricula || ""))
      await api.patch(`/veiculos/${a.veiculo.id}/matricula`, { matricula: identMat });
    if (!a.veiculo?.id && identMat) {
      const { data: v } = await api.post("/veiculos", { matricula: identMat, cliente_id: a.cliente?.id || null });
      await api.put(`/atendimentos/${a.id}`, { veiculo_id: v.id });
    }
  };
  const outroCliente = async () => {
    try {
      const { data } = await api.post("/clientes", { nome: identNome.trim() });
      await api.put(`/atendimentos/${a.id}/cliente`, { cliente_id: data.id });
      await guardarMatricula();
      toast.success(`Novo cliente "${identNome.trim()}" associado a este pedido. "${a.cliente.nome}" mantém-se.`);
      setIdentDlg(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const salvarIdent = async (confirmar = false) => {
    try {
      if (a.cliente?.id && identNome.trim() && identNome.trim() !== a.cliente?.nome) {
        try {
          await api.patch(`/clientes/${a.cliente.id}/nome`, { nome: identNome.trim(), confirmar, pedido_id: a.id });
        } catch (err) {
          if (err.response?.status === 409) { setIdentAviso(err.response.data.detail); return; }
          throw err;
        }
      }
      await guardarMatricula();
      toast.success("Dados corrigidos.");
      setIdentDlg(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const reativar = async () => {
    await fila.current;
    try {
      const { data } = await api.post(`/atendimentos/${id}/reativar`);
      const r = await api.get(`/atendimentos/${id}`);
      setA(r.data || data);
      toast.success("Pedido reativado — as linhas voltaram a aguardar resposta.");
    } catch (err) { toast.error(apiError(err)); }
  };

  const changeState = (lid, estado, mot) => {
    fila.current = fila.current.then(async () => {
      try {
        const { data } = await api.post(`/atendimentos/${id}/linhas/${lid}/estado`, { estado, motivo: mot });
        const r = await api.get(`/atendimentos/${id}`);
        setA(r.data || data);
        toast.success("Estado atualizado.");
      } catch (err) { toast.error(apiError(err)); }
    });
    return fila.current;
  };

  // grava só o campo alterado, em fila, para nunca sobrepor alterações anteriores
  const saveLineField = (lid, patch) => {
    setGravando((n) => n + 1);
    fila.current = fila.current.then(async () => {
      try {
        const { data } = await api.patch(`/atendimentos/${id}/linhas/${lid}`, patch);
        setA((prev) => ({ ...data, cliente: prev.cliente, veiculo: prev.veiculo, eventos: prev.eventos }));
      } catch (err) {
        toast.error(`Linha NÃO gravada: ${apiError(err)}`, { duration: 10000 });
      } finally { setGravando((n) => n - 1); }
    });
    return fila.current;
  };

  const addLinha = async () => {
    await fila.current;
    try {
      const { data } = await api.post(`/atendimentos/${id}/linhas`, { qtd: 1, descricao: "NOVA LINHA", desconto: 0, estado: "pendente" });
      setA((prev) => ({ ...data, cliente: prev.cliente, veiculo: prev.veiculo, eventos: prev.eventos }));
    } catch (err) { toast.error(apiError(err)); }
  };

  const criarEncomenda = async () => {
    const itens = Object.keys(selLinhas).filter((k) => selLinhas[k]).map((lid) => ({ atendimento_id: id, linha_id: lid }));
    if (!itens.length) { toast.error("Selecione linhas aprovadas."); return; }
    try {
      const { data } = await api.post("/encomendas", { fornecedor_id: encForn || null, origem_id: encOrigem || null, itens });
      toast.success(`Encomenda ${numRef(data.numero, data.codigo)} criada.`);
      setEncDlg(false); setSelLinhas({}); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const aprovadas = (a.linhas || []).filter((l) => l.estado === "aprovado" && !l.encomenda_id);

  const abrirDG = async () => {
    if (!dgDlg) return;
    const isDev = dgDlg.tipo === "devolucao";
    try {
      const { data } = await api.post(
        `/atendimentos/${id}/linhas/${dgDlg.lid}/devolucao-garantia`,
        { tipo: dgDlg.tipo, motivo: dgMotivo || null, substituir: isDev && dgSubst === true, qtd: isDev ? Number(dgDlg.qtd) || null : null }
      );
      setDgDlg(null); setDgMotivo(""); setDgSubst(null);
      if (data.novo) {
        toast.success(isDev ? `Devolução registada e pedido de substituição ${numRef(data.novo.numero, data.novo.codigo)} criado.` : `Garantia ${numRef(data.novo.numero, data.novo.codigo)} criada.`);
        navigate(`/pedido/${data.novo.id}`);
      } else {
        toast.success("Devolução registada — aguarda nota de crédito.");
        load();
      }
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-4">
      <div className="col-span-12 2xl:col-span-9 min-w-0">
        <button onClick={() => navigate("/")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3" data-testid="btn-voltar">
          <ArrowLeft size={15} /> Dashboard
        </button>
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-sky-50 border-b border-sky-100 px-5 py-3 flex items-start justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700">Atendimento / Pedido</div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-1">
                <span className="text-xl font-bold text-slate-900">{numRef(a.numero, a.codigo)}</span>
                {a.estado === "arquivado" && <span className="text-[10px] font-semibold uppercase rounded border border-zinc-300 bg-zinc-100 text-zinc-600 px-1.5 py-0.5" data-testid="pedido-arquivado-badge">{(a.linhas || []).some((l) => l.desistencia) ? "Arquivado · desistência" : "Arquivado · sem resposta"}</span>}
                {a.tipo && TIPOS[a.tipo] && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${TIPOS[a.tipo].badge}`} data-testid="pedido-tipo-badge">
                    {TIPOS[a.tipo].label}
                  </span>
                )}
                {a.veiculo?.matricula && <span className="plate">{a.veiculo.matricula}</span>}
                <span className="text-sm text-slate-600">{a.veiculo?.marca} {a.veiculo?.modelo} {a.veiculo?.motorizacao}</span>
                <VinChip key={a.veiculo?.vin || "x"} veiculo={a.veiculo} onSaved={(v) => setA((prev) => ({ ...prev, veiculo: v }))} />
                <span className={`text-xs ${PRIORIDADES[a.prioridade]?.cls}`}>{PRIORIDADES[a.prioridade]?.label}</span>
              </div>
              {a.pedido_origem_id && (
                <button onClick={() => navigate(`/pedido/${a.pedido_origem_id}`)} data-testid="link-pedido-origem"
                  className="flex items-center gap-1 text-[11px] text-sky-600 hover:text-sky-800 mt-1">
                  <ExternalLink size={11} /> Origem: pedido {numRef(a.pedido_origem_numero, a.pedido_origem_codigo)}
                </button>
              )}
              <div className="text-xs text-slate-500 mt-1">{a.descricao_inicial}</div>
              <div className="text-[11px] text-slate-400 mt-0.5" data-testid="pedido-data">Criado em {fmtData(a.created_at)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-slate-400 font-semibold">Total</div>
              <div className="text-lg font-bold text-slate-900">{eur(total)}</div>
            </div>
          </div>

          <div className="px-5 py-2.5 border-b border-slate-100 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <div><span className="text-[10px] uppercase text-slate-400 font-semibold block">Cliente</span>
              <span className="font-medium text-slate-800 inline-flex items-center gap-1">
                {a.cliente?.nome || (podeEditarIdent
                  ? <button onClick={abrirIdent} className="text-xs text-sky-600 hover:underline" data-testid="btn-associar-cliente">+ Associar cliente</button>
                  : "—")}
                {podeEditarIdent && a.cliente && (
                  <button onClick={abrirIdent} data-testid="btn-editar-ident" className="text-slate-400 hover:text-sky-600" title="Corrigir nome/matrícula">
                    <Pencil size={12} />
                  </button>
                )}
              </span>
            </div>
            <div><span className="text-[10px] uppercase text-slate-400 font-semibold block">Contacto</span><span className="text-slate-700">{a.cliente?.telefone || "—"}</span></div>
            <div><span className="text-[10px] uppercase text-slate-400 font-semibold block">Responsável</span><span className="text-slate-700">{a.responsavel_atual}</span></div>
            <div className="ml-auto flex flex-wrap gap-2">
              {gravando > 0 && <span className="text-[11px] text-amber-600 self-center" data-testid="linhas-a-gravar">A gravar…</span>}
              <ProximoPasso pedido={a} antes={() => fila.current} onDone={() => load()} />
              {(a.linhas || []).some((l) => l.estado === "sem_resposta" || (l.estado === "cancelado" && l.desistencia)) && (
                <Button size="sm" className="bg-violet-600 hover:bg-violet-700 h-8 gap-1 text-xs" onClick={reativar} data-testid="btn-reativar">Reativar</Button>
              )}
              <Button size="sm" variant="outline" onClick={addLinha} data-testid="btn-add-linha" className="h-8 gap-1 text-xs"><Plus size={13} /> Linha</Button>
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 h-8 gap-1 text-xs" onClick={() => setEncDlg(true)} data-testid="btn-converter-encomenda" disabled={!aprovadas.length}>
                <Layers size={13} /> Encomenda ({aprovadas.length})
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                <tr>
                  <th className="text-left px-2 py-2 font-semibold w-10">Qtd</th>
                  <th className="text-left px-2 py-2 font-semibold">Descrição</th>
                  <th className="text-left px-2 py-2 font-semibold">Ref. marca</th>
                  <th className="text-left px-2 py-2 font-semibold">Forn.</th>
                  <th className="text-right px-2 py-2 font-semibold w-16">P. Unit</th>
                  <th className="text-right px-2 py-2 font-semibold w-12">Desc%</th>
                  <th className="text-right px-2 py-2 font-semibold w-20">Total</th>
                  <th className="text-left px-2 py-2 font-semibold w-28">Estado</th>
                  <th className="text-left px-1 py-2 font-semibold w-12">Prev.</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(a.linhas || []).map((l) => (
                  <tr key={l.id} className={`border-t border-slate-100 relative ${l.estado === "cancelado" ? "opacity-50" : ""}`} data-testid={`linha-row-${l.id}`}>
                    <td className={`px-2 py-1.5 border-l-4 ${LINE_STATES[l.estado]?.strip.replace("bg-", "border-l-") || "border-l-slate-300"}`}>
                      <input key={`q-${l.qtd}`} data-testid={`linha-qtd-${l.id}`} className="w-8 bg-transparent focus:bg-white focus:ring-1 rounded px-1" defaultValue={l.qtd} onBlur={(e) => saveLineField(l.id, { qtd: parseFloat(e.target.value) || 1 })} />
                    </td>
                    <td className="px-2 py-1.5 font-medium text-slate-800 min-w-[10rem]">
                      <input key={`d-${l.descricao}`} data-testid={`linha-descricao-${l.id}`} className="w-full bg-transparent focus:bg-white focus:ring-1 rounded px-1 uppercase" defaultValue={l.descricao} onBlur={(e) => saveLineField(l.id, { descricao: e.target.value })} />
                    </td>
                    <td className="px-2 py-1.5 font-mono min-w-[6.5rem]">
                      <input data-testid={`linha-ref-${l.id}`} className="w-full bg-transparent focus:bg-white focus:ring-1 rounded px-1 font-mono uppercase" defaultValue={l.ref_marca || ""} onBlur={(e) => { e.target.value = e.target.value.replace(/\s+/g, "").toUpperCase(); saveLineField(l.id, { ref_marca: e.target.value }); }} />
                    </td>
                    <td className="px-1 py-1.5">
                      <OrigemPicker fornecedores={fornecedores} fornecedorId={l.fornecedor_id} origemId={l.origem_id}
                        onChange={(fid, oid) => saveLineField(l.id, { fornecedor_id: fid, origem_id: oid })}
                        className="h-7 text-xs border-0 bg-transparent px-1 gap-0.5 w-auto max-w-[6.5rem]" testid={`linha-fornecedor-${l.id}`} />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input key={`p-${l.preco_unit}`} data-testid={`linha-preco-${l.id}`} className="w-14 text-right bg-transparent focus:bg-white focus:ring-1 rounded px-1" defaultValue={l.preco_unit ?? ""} onBlur={(e) => saveLineField(l.id, { preco_unit: e.target.value === "" ? null : parseFloat(e.target.value) })} />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input key={`ds-${l.desconto}`} data-testid={`linha-desconto-${l.id}`} className="w-9 text-right bg-transparent focus:bg-white focus:ring-1 rounded px-1" defaultValue={l.desconto || 0} onBlur={(e) => saveLineField(l.id, { desconto: parseFloat(e.target.value) || 0 })} />
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold whitespace-nowrap" data-testid={`linha-total-${l.id}`}>{eur(l.total)}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium ${LINE_STATES[l.estado]?.badge}`}>
                          {LINE_STATES[l.estado]?.label || l.estado}
                        </span>
                        {l.estado === "recebido" && (
                          <Button size="sm" className="h-6 px-2 text-[10px] bg-indigo-600 hover:bg-indigo-700" data-testid={`btn-faturar-${l.id}`} onClick={() => changeState(l.id, "faturado")}>Faturar</Button>
                        )}
                      </div>
                      {l.motivo_cancelamento && <div className="text-[10px] text-rose-500 mt-0.5">{l.motivo_cancelamento}</div>}
                      {l.devolucao && <div className="text-[10px] text-orange-600 mt-0.5" data-testid={`linha-devolucao-${l.id}`}>Devolução: {l.devolucao.motivo}{l.nota_credito ? ` · NC ${l.nota_credito.numero}` : ""}</div>}
                    </td>
                    <td className="px-1 py-1.5">
                      <PrazoLinha linha={l} fornecedor={fornecedores.find((f) => f.id === l.fornecedor_id)} onSave={(p) => saveLineField(l.id, p)} />
                    </td>
                    <td className="px-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger data-testid={`linha-menu-${l.id}`}><MoreVertical size={15} className="text-slate-400" /></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {NEXT_STATES.map((s) => (
                            <DropdownMenuItem key={s} data-testid={`estado-${s}-${l.id}`}
                              onClick={() => { if (s === "cancelado") { setCancelDlg(l.id); setMotivo(""); } else changeState(l.id, s); }}>
                              <span className={`h-2 w-2 rounded-full mr-2 ${LINE_STATES[s]?.strip}`} />
                              {LINE_STATES[s]?.label}
                            </DropdownMenuItem>
                          ))}
                          {isAdmin && <>
                            <DropdownMenuSeparator />
                            <div className="px-2 py-1 text-[10px] uppercase text-slate-400">Corrigir estado automático</div>
                            {AUTO_STATES.map((s) => (
                              <DropdownMenuItem key={s} data-testid={`corrigir-${s}-${l.id}`} onClick={() => changeState(l.id, s)}>
                                <span className={`h-2 w-2 rounded-full mr-2 ${LINE_STATES[s]?.strip}`} />
                                <span className="text-slate-500">{LINE_STATES[s]?.label}</span>
                              </DropdownMenuItem>
                            ))}
                          </>}
                          <DropdownMenuSeparator />
                          {["recebido", "faturado", "entregue"].includes(l.estado) && (
                          <DropdownMenuItem data-testid={`devolucao-${l.id}`}
                            onClick={() => { setDgDlg({ lid: l.id, tipo: "devolucao", descricao: l.descricao, qtdTotal: l.qtd || 1, qtd: l.qtd || 1 }); setDgMotivo(""); setDgSubst(null); }}>
                            <RotateCcw size={14} className="mr-2 text-orange-500" /> Registar devolução
                          </DropdownMenuItem>
                          )}
                          <DropdownMenuItem data-testid={`garantia-${l.id}`}
                            onClick={() => { setDgDlg({ lid: l.id, tipo: "garantia", descricao: l.descricao }); setDgMotivo(""); }}>
                            <ShieldCheck size={14} className="mr-2 text-purple-500" /> Abrir garantia
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <OrcamentoCliente pedido={a} />
          <NotasPedido key={a.id} pedidoId={a.id} notas={a.notas} disabled={a.estado === "concluido"} />
        </div>
      </div>

      {/* history */}
      <div className="col-span-12 2xl:col-span-3">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 sticky top-20">
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3 flex items-center gap-1.5"><History size={14} /> Histórico</h3>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            {(a.eventos || []).map((e) => (
              <div key={e.id} className="text-xs border-l-2 border-slate-200 pl-2.5">
                <div className="text-slate-700">{e.descricao}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{e.user_name} · {tempoDesde(e.created_at)}</div>
              </div>
            ))}
            {(!a.eventos || !a.eventos.length) && <div className="text-xs text-slate-400">Sem eventos.</div>}
          </div>
        </div>
      </div>

      {/* cancel dialog */}
      <Dialog open={!!cancelDlg} onOpenChange={(o) => !o && setCancelDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancelar linha</DialogTitle></DialogHeader>
          <Input placeholder="Motivo do cancelamento" value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="input-motivo-cancelamento" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDlg(null)}>Voltar</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" data-testid="btn-confirmar-cancelamento"
              onClick={() => { changeState(cancelDlg, "cancelado", motivo); setCancelDlg(null); }}>Cancelar linha</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* corrigir cliente / matrícula */}
      <Dialog open={identDlg} onOpenChange={setIdentDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Corrigir cliente e matrícula</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Editável enquanto o pedido não estiver faturado. Corrige o registo do cliente e do veículo.</p>
          <div className="space-y-2">
            <EscolherCliente pedidoId={a.id} atual={a.cliente} onDone={() => { setIdentDlg(false); load(); }} />
            {a.cliente?.id && (
            <div>
              <label className="text-[11px] text-slate-500">Corrigir o nome do cliente atual</label>
              <Input value={identNome} onChange={(e) => { setIdentNome(e.target.value); setIdentAviso(null); }} data-testid="ident-nome" />
              <p className="text-[11px] text-slate-400 mt-0.5">Só para corrigir erros de escrita. Se for outra pessoa/empresa, use "Trocar para outro cliente" acima.</p>
            </div>
            )}
            {identAviso && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 space-y-2" data-testid="ident-aviso">
                <p>{identAviso}</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="h-7 text-xs bg-sky-600 hover:bg-sky-700" onClick={outroCliente} data-testid="ident-outro-cliente">É outro cliente: criar "{identNome}" só neste pedido</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => salvarIdent(true)} data-testid="ident-corrigir-todos">É o mesmo: corrigir em todos</Button>
                </div>
              </div>
            )}
            <div>
              <label className="text-[11px] text-slate-500">Matrícula</label>
              <Input value={identMat} onChange={(e) => setIdentMat(formatMatricula(e.target.value))} placeholder="AA-00-00" maxLength={8} className="font-mono uppercase" data-testid="ident-matricula" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIdentDlg(false)}>Cancelar</Button>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={() => salvarIdent(false)} data-testid="btn-salvar-ident">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* devolução / garantia dialog */}
      <Dialog open={!!dgDlg} onOpenChange={(o) => !o && setDgDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dgDlg?.tipo === "garantia" ? "Abrir garantia" : "Registar devolução"}</DialogTitle>
          </DialogHeader>
          {dgDlg?.tipo === "garantia" ? (
            <>
              <p className="text-xs text-slate-500">
                Vai criar um <b>novo pedido de garantia</b> ligado a este ({numRef(a.numero, a.codigo)}),
                para a linha <b>{dgDlg?.descricao}</b>. O pedido original mantém-se inalterado.
              </p>
              <Input placeholder="Motivo (opcional)" value={dgMotivo} onChange={(e) => setDgMotivo(e.target.value)} data-testid="input-dg-motivo" />
            </>
          ) : (
            <>
              <p className="text-xs text-slate-500">A linha <b>{dgDlg?.descricao}</b> passa a <b>Devolvido · aguarda nota de crédito</b> e aparece na página Notas de crédito.</p>
              <Textarea placeholder="Motivo da devolução (obrigatório)" value={dgMotivo} onChange={(e) => setDgMotivo(e.target.value)} rows={2} data-testid="input-dg-motivo" />
              {dgDlg?.qtdTotal > 1 && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-slate-700">Quantidade a devolver</span>
                  <Input type="number" min={1} max={dgDlg.qtdTotal} step="1" value={dgDlg.qtd} className="h-8 w-20"
                    onChange={(e) => setDgDlg({ ...dgDlg, qtd: e.target.value })} data-testid="input-dg-qtd" />
                  <span className="text-slate-500">de {dgDlg.qtdTotal}</span>
                </div>
              )}
              {dgDlg?.qtdTotal > 1 && Number(dgDlg.qtd) < dgDlg.qtdTotal && (
                <p className="text-[11px] text-slate-500" data-testid="dg-qtd-info">A linha divide-se: {dgDlg.qtdTotal - Number(dgDlg.qtd || 0)} un. ficam como estão e {dgDlg.qtd} un. passam a devolvidas.</p>
              )}
              <div className="text-sm font-medium text-slate-700">Precisa de peça para substituição?</div>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={dgSubst === true ? "default" : "outline"} onClick={() => setDgSubst(true)} data-testid="dg-subst-sim">Sim, criar pedido novo</Button>
                <Button type="button" size="sm" variant={dgSubst === false ? "default" : "outline"} onClick={() => setDgSubst(false)} data-testid="dg-subst-nao">Não</Button>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDgDlg(null)}>Voltar</Button>
            <Button className={dgDlg?.tipo === "garantia" ? "bg-purple-600 hover:bg-purple-700" : "bg-orange-600 hover:bg-orange-700"}
              disabled={dgDlg?.tipo === "devolucao" && (!dgMotivo.trim() || dgSubst === null || !(Number(dgDlg.qtd) >= 1 && Number(dgDlg.qtd) <= dgDlg.qtdTotal))}
              data-testid="btn-confirmar-dg" onClick={abrirDG}>
              {dgDlg?.tipo === "garantia" ? "Criar garantia" : "Registar devolução"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* encomenda dialog */}
      <Dialog open={encDlg} onOpenChange={setEncDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Converter em Encomenda</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Selecione as linhas aprovadas a encomendar.</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {aprovadas.map((l) => (
              <label key={l.id} className="flex items-center gap-2 text-sm p-2 rounded border border-slate-200 cursor-pointer" data-testid={`enc-linha-${l.id}`}>
                <input type="checkbox" checked={!!selLinhas[l.id]} onChange={(e) => setSelLinhas({ ...selLinhas, [l.id]: e.target.checked })} />
                <span className="flex-1">{l.descricao} <span className="font-mono text-slate-500">{l.ref_marca}</span></span>
                <span className="text-slate-600">{eur(l.total)}</span>
              </label>
            ))}
            {!aprovadas.length && <div className="text-xs text-slate-400">Nenhuma linha aprovada.</div>}
          </div>
          <OrigemPicker fornecedores={fornecedores} fornecedorId={encForn} origemId={encOrigem}
            onChange={(fid, oid) => { setEncForn(fid); setEncOrigem(oid || ""); }}
            testid="select-enc-fornecedor" placeholder="Fornecedor / armazém (opcional)" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEncDlg(false)}>Voltar</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={criarEncomenda} data-testid="btn-criar-encomenda">Criar encomenda</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
